import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2, MapPin, Calendar, Ticket, ShieldCheck, ArrowRight,
  Share2, Copy, Check, Eye, Lock, BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useLanguage } from "../contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PublicListingPage = ({ user, onLogout, refreshUserData }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const isFr = language === "fr";
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/public/billet/${slug}`);
        setListing(res.data);
      } catch (e) {
        setError(e.response?.status === 404 ? "not_found" : "error");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [slug]);

  const handleBuy = () => {
    if (!user) {
      toast.info("Connectez-vous ou créez un compte pour acheter ces billets");
      navigate(`/auth?returnTo=/billet/${slug}`);
      return;
    }
    // Initiate a transfer request prefilled with listing data
    navigate(`/dashboard?openTransfer=1&listing=${slug}`);
  };

  const copyLink = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success("Lien copié");
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
        <div className="flex items-center justify-center pt-32">
          <Loader2 className="h-8 w-8 animate-spin text-brand-700" />
        </div>
      </div>
    );
  }

  if (error || !listing) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
        <div className="flex-1 flex items-center justify-center px-4 pt-20">
          <div className="text-center max-w-md">
            <Ticket className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Annonce introuvable</h1>
            <p className="text-gray-600 mb-6">Le lien n'est pas valide ou l'annonce a été retirée.</p>
            <button onClick={() => navigate('/')} className="bg-gray-900 hover:bg-black text-white font-semibold px-6 py-3 rounded-lg">
              Retour à l'accueil
            </button>
          </div>
        </div>
        <Footer variant="simple" />
      </div>
    );
  }

  if (listing.status && listing.status !== "active") {
    const statusLabel = {
      sold: "Cette annonce a été vendue",
      cancelled: "Cette annonce a été annulée",
      expired: "Cette annonce a expiré",
    }[listing.status] || "Annonce non disponible";
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
        <div className="flex-1 flex items-center justify-center px-4 pt-20">
          <div className="text-center max-w-md">
            <Ticket className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">{statusLabel}</h1>
            {listing.event_name && <p className="text-gray-600 mb-2">{listing.event_name}</p>}
            <p className="text-gray-500 mb-6 text-sm">Découvrez d'autres annonces disponibles.</p>
            <button onClick={() => navigate('/')} className="bg-gray-900 hover:bg-black text-white font-semibold px-6 py-3 rounded-lg">
              Voir d'autres événements
            </button>
          </div>
        </div>
        <Footer variant="simple" />
      </div>
    );
  }

  const total = (listing.ticket_count || 1) * (listing.price_per_ticket || 0);

  return (
    <div className="min-h-screen bg-white">
      <Navbar user={user} onLogout={onLogout} refreshUserData={refreshUserData} />
      <div className="h-16 lg:h-[68px]" />

      <section className="bg-gradient-to-br from-gray-900 to-gray-800 text-white py-12 md:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-brand-300/15 border border-brand-300/30 rounded-full mb-4">
            <ShieldCheck className="h-3.5 w-3.5 text-brand-300" />
            <span className="text-xs font-semibold text-brand-200">Annonce Middleman vérifiée</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-3" data-testid="listing-event-title">
            {listing.event_name}
          </h1>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-gray-300 text-sm">
            {listing.event_date && <div className="flex items-center gap-1.5"><Calendar className="h-4 w-4 text-brand-300" />{listing.event_date}{listing.event_time ? ` • ${listing.event_time}` : ''}</div>}
            {listing.event_venue && <div className="flex items-center gap-1.5"><MapPin className="h-4 w-4 text-brand-300" />{listing.event_venue}{listing.event_city ? `, ${listing.event_city}` : ''}</div>}
            <div className="flex items-center gap-1.5"><Eye className="h-4 w-4 text-brand-300" />{listing.views || 0} vues</div>
          </div>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white border border-gray-200 rounded-2xl p-6 md:p-8">
              <h2 className="font-bold text-lg text-gray-900 mb-4">Détails des billets</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Quantité</p>
                  <p className="font-bold text-gray-900">{listing.ticket_count}</p>
                </div>
                {listing.section && (
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Section</p>
                    <p className="font-bold text-gray-900">{listing.section}</p>
                  </div>
                )}
                {listing.row && (
                  <div>
                    <p className="text-xs text-gray-500 mb-0.5">Rangée</p>
                    <p className="font-bold text-gray-900">{listing.row}</p>
                  </div>
                )}
                {listing.seats && (
                  <div className="col-span-2 sm:col-span-3">
                    <p className="text-xs text-gray-500 mb-0.5">Sièges</p>
                    <p className="font-bold text-gray-900">{listing.seats}</p>
                  </div>
                )}
              </div>
              {listing.description && (
                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500 mb-1.5">Description</p>
                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{listing.description}</p>
                </div>
              )}
            </div>

            <div className="bg-brand-50/60 border border-brand-200 rounded-2xl p-6">
              <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-brand-700" />
                {isFr ? 'Transaction 100% sécurisée' : '100% secure transaction'}
              </h3>
              <ul className="space-y-2.5 text-sm text-gray-700">
                <li className="flex items-start gap-2"><BadgeCheck className="h-4 w-4 text-brand-700 mt-0.5 flex-shrink-0" />Vos billets ne sont transférés qu'une fois le paiement vérifié.</li>
                <li className="flex items-start gap-2"><Lock className="h-4 w-4 text-brand-700 mt-0.5 flex-shrink-0" />Aucun échange direct de coordonnées personnelles entre les parties.</li>
                <li className="flex items-start gap-2"><ShieldCheck className="h-4 w-4 text-brand-700 mt-0.5 flex-shrink-0" />Middleman intermédiaire de confiance — vérification de chaque transaction.</li>
              </ul>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="sticky top-24 bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
              <p className="text-xs text-gray-500 mb-1">Prix par billet</p>
              <p className="text-3xl font-extrabold text-gray-900 mb-1">{listing.price_per_ticket} {listing.currency}</p>
              <div className="flex items-baseline justify-between pb-4 mb-4 border-b border-gray-100">
                <p className="text-sm text-gray-600">Total ({listing.ticket_count} billet{listing.ticket_count > 1 ? 's' : ''})</p>
                <p className="text-lg font-bold text-gray-900">{total.toFixed(2)} {listing.currency}</p>
              </div>

              <button
                onClick={handleBuy}
                data-testid="public-buy-btn"
                className="group w-full inline-flex items-center justify-center gap-2 bg-brand-300 hover:bg-brand-400 text-gray-900 font-bold py-3.5 rounded-lg transition-all mb-3"
              >
                {isFr ? 'Acheter ces billets' : 'Buy these tickets'}
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </button>
              <button
                onClick={copyLink}
                className="w-full inline-flex items-center justify-center gap-1.5 border border-gray-300 hover:bg-gray-50 text-gray-700 font-semibold py-2.5 rounded-lg text-sm"
              >
                {copied ? <><Check className="h-4 w-4" /> Lien copié</> : <><Share2 className="h-4 w-4" /> Partager le lien</>}
              </button>

              <div className="text-xs text-gray-500 mt-4 leading-relaxed">
                Vendu par <span className="font-semibold text-gray-700">{listing.seller_display_name}</span>. Identité vérifiée par Middleman.
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer variant="full" />
    </div>
  );
};

export default PublicListingPage;
